import type { ContentType } from "../lib/types.js";
import { PARAM_FIELDS, type DeliverableParamForm } from "../lib/deliverableParams.js";

const inputCls =
  "w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 " +
  "outline-none focus:ring-2 focus:ring-(--accent) dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100";

/** Placeholder text per field, so the seeded route default is discoverable. */
const HINTS: Record<string, string> = {
  width: "e.g. 1024 (leave blank for the route default)",
  height: "e.g. 1024 (leave blank for the route default)",
  voice: "leave blank for the route's seeded default voice",
  durationSec: "5",
  format: "optional, e.g. png / mp3 / markdown",
};

const LABELS: Record<string, string> = {
  width: "Width (px)",
  height: "Height (px)",
  voice: "Voice",
  durationSec: "Duration (seconds)",
  format: "Format",
};

/**
 * Per-content-type parameters editor. Writes raw strings into `form`; the parent
 * turns them into a `params` object with `buildParams` at save time, which is
 * where an empty field is dropped and a negative dimension is rejected.
 */
export function DeliverableParamsEditor({
  contentType,
  form,
  onChange,
}: {
  contentType: ContentType;
  form: DeliverableParamForm;
  onChange: (form: DeliverableParamForm) => void;
}) {
  const fields = [...(PARAM_FIELDS[contentType] ?? []), { key: "format", kind: "text" as const }];

  function set(key: string, value: string) {
    onChange({ ...form, [key]: value });
  }

  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {fields.map(({ key, kind }) => (
        <div key={key}>
          <label className="mb-1 block text-xs text-gray-500 dark:text-gray-400">
            {LABELS[key] ?? key}
          </label>
          <input
            className={inputCls}
            type={kind === "number" ? "number" : "text"}
            min={kind === "number" ? 0 : undefined}
            value={form[key] ?? ""}
            placeholder={HINTS[key]}
            onChange={(e) => set(key, e.target.value)}
          />
        </div>
      ))}
    </div>
  );
}
